import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import FeedbackPin from './FeedbackPin';

import styles from './styles.module.scss';

const CATEGORY_MAPPING = {
  error: { class: 'error' },
  suggestion: { class: 'warning' },
  positive: { class: 'positive' },
  'deeper-insights': { class: 'deeper-insights' },
};

const ImageCarousel = ({
  feedback,
  images,
  onBackClick,
  selectedImageIndex = 0,
  onImageIndexChange,
  onPinClick,
  isLoading = false,
}) => {
  const [currentImageAspectRatio, setCurrentImageAspectRatio] = useState(null);
  const [thumbnailsOverflow, setThumbnailsOverflow] = useState(false);
  const thumbnailsWrapperRef = useRef(null);
  const thumbnailsInnerRef = useRef(null);

  const thumbnailIndices = useMemo(
    () => [...Array(images.length).keys()],
    [images.length]
  );

  // Detect if thumbnails exceed parent container width
  useEffect(() => {
    const wrapper = thumbnailsWrapperRef.current;
    const inner = thumbnailsInnerRef.current;

    if (!wrapper || !inner || !images.length) {
      setThumbnailsOverflow(false);

      return;
    }

    const checkOverflow = () => {
      const availableWidth = wrapper.clientWidth - 32;
      const contentWidth = inner.scrollWidth;

      setThumbnailsOverflow(contentWidth > Math.max(0, availableWidth));
    };

    const rafId = requestAnimationFrame(() => {
      checkOverflow();
    });

    // eslint-disable-next-line compat/compat -- ResizeObserver used for thumbnail overflow detection
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(checkOverflow);
    });

    resizeObserver.observe(wrapper);

    resizeObserver.observe(inner);

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
    };
  }, [images.length]);

  // Reset aspect ratio when image changes to avoid flash of incorrect styling
  useEffect(() => {
    setCurrentImageAspectRatio(null);
  }, [selectedImageIndex]);

  // Get feedback items for current page with pin locations, handling dismissed findings and overlapping positions
  const currentPageFeedback = useMemo(() => {
    if (!feedback || !Array.isArray(feedback) || !images[selectedImageIndex]) {
      return [];
    }

    const currentPage = images[selectedImageIndex].page || selectedImageIndex + 1;

    // Filter for current page, exclude dismissed findings, and only include items with pin locations
    const activeFeedback = feedback.filter(
      item => item.page === currentPage
      && !item.dismiss
      && item.dismiss !== true
      && item.pin_location
    );

    // Handle overlapping pin positions
    const positionTracker = new Map();

    return activeFeedback.map(feedbackItem => {
      const pinLocation = feedbackItem.pin_location;

      const positionKey = `${pinLocation.x_percent}-${pinLocation.y_percent}`;

      // Find the original index of this feedback item in the full feedback array
      const originalIndex = feedback.findIndex(item => item === feedbackItem);
      const feedbackNumber = originalIndex + 1; // Convert to 1-based numbering

      // Determine category and severity class (matching FeedbackBox logic)
      const getCategory = item => {
        if (item.isDeeperInsight) {
          return 'deeper-insights';
        }

        return item.category || '';
      };

      const category = getCategory(feedbackItem);
      const severityClass = category && CATEGORY_MAPPING[category]
        ? CATEGORY_MAPPING[category].class
        : '';

      if (positionTracker.has(positionKey)) {
        // If position is already used, offset the new pin to the right
        const offset = positionTracker.get(positionKey) * 8; // 5% offset for each overlapping pin

        positionTracker.set(positionKey, positionTracker.get(positionKey) + 1);

        return {
          ...feedbackItem,
          feedbackIndex: originalIndex,
          feedbackNumber,
          severityClass,
          adjustedLocation: {
            x_percent: Math.min(pinLocation.x_percent + offset, 95), // Don't go beyond 95% to avoid edge cutoff
            y_percent: pinLocation.y_percent,
          },
        };
      }
      positionTracker.set(positionKey, 1);

      return {
        ...feedbackItem,
        feedbackIndex: originalIndex,
        feedbackNumber,
        severityClass,
        adjustedLocation: pinLocation,
      };
    });
  }, [feedback, selectedImageIndex, images]);

  const handleThumbnailClick = index => {
    // Allow clicking on any page index
    handleImageSelect(index);
  };

  if (isLoading) {
    return (
      <div className={cn(styles['detail-image-wrapper'])}>
        <div className={styles['detail-image-placeholder']}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <div
              style={{
                width: '16px',
                height: '16px',
                border: '2px solid #E2E8F0',
                borderTop: '2px solid #3B82F6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
            Loading images...
          </div>
        </div>
      </div>
    );
  }

  if (!images || images.length === 0) {
    return (
      <div className={cn(styles['detail-image-wrapper'])}>
        <div className={styles['detail-image-placeholder']}>
          No images available
        </div>
      </div>
    );
  }

  const currentImage = images[selectedImageIndex];

  return (
    <>
      <div className={styles['detail-preview-header']}>
        <button
          onClick={onBackClick}
          className={styles['detail-back-button']}
        >
          ←
        </button>
        {currentImage?.page && (
          <span className={styles['detail-page-indicator']}>
            {currentImage.page}
            {' '}
            of
            {' '}
            {images.length}
          </span>
        )}
      </div>
      <div className={cn('flex flex-1 flex-col gap-4 overflow-auto')}>
        {/* Main Image Display */}
        <div className={styles['detail-image-wrapper']}>
          <div className={cn(styles['inner'], { [styles['full-height']]: currentImageAspectRatio < 1 })}>
            <img
              key={selectedImageIndex}
              src={currentImage.url}
              alt={currentImage.alt || `Design ${selectedImageIndex + 1}`}
              className={styles['detail-image']}
              loading={selectedImageIndex <= 1 ? 'eager' : 'lazy'}
              onLoad={e => {
                // Simple way to get original image dimensions
                const { naturalWidth, naturalHeight } = e.target;
                const aspectRatio = naturalWidth / naturalHeight;

                // Update aspect ratio state to conditionally apply class
                setCurrentImageAspectRatio(aspectRatio);
              }}
            />
            {/* Feedback Pins */}
            {currentPageFeedback.map((feedbackItem, index) => (
              <FeedbackPin
                key={`${feedbackItem.id || index}-pin`}
                number={feedbackItem.feedbackNumber}
                x={feedbackItem.adjustedLocation.x_percent}
                y={feedbackItem.adjustedLocation.y_percent}
                severity={feedbackItem.severityClass}
                onClick={
                  onPinClick && feedbackItem.feedbackIndex != null
                    ? () => onPinClick(feedbackItem.feedbackIndex)
                    : undefined
                }
              />
            ))}
          </div>
        </div>

        <div
          ref={thumbnailsWrapperRef}
          className={cn(styles['detail-thumbnails-wrapper'], {
            [styles['detail-thumbnails-wrapper--scrollable']]: thumbnailsOverflow,
          })}
        >
          <div
            ref={thumbnailsInnerRef}
            className={cn(styles['detail-thumbnails'], {
              [styles['detail-thumbnails--scrollable']]: thumbnailsOverflow,
            })}
          >
            {thumbnailIndices.map(index => {
              const image = images[index];
              const isSelected = index === selectedImageIndex;

              return (
                <button
                  key={index}
                  type="button"
                  className={cn(styles['detail-thumbnail-item'], {
                    [styles['detail-thumbnail-item--selected']]: isSelected,
                  })}
                  onClick={() => handleThumbnailClick(index)}
                  aria-label={`Go to image ${index + 1}`}
                  aria-current={isSelected ? 'true' : undefined}
                >
                  <div
                    className={cn(styles['detail-thumbnail-box'], {
                      [styles['detail-thumbnail-box--selected']]: isSelected,
                    })}
                  >
                    <img
                      src={image.url}
                      alt={image.alt || `Design ${index + 1}`}
                      className={styles['detail-thumbnail-img']}
                      loading="lazy"
                    />
                  </div>
                  <div className={styles['detail-thumbnail-number']}>
                    {index + 1}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );

  // Handle image selection to show relevant feedback
  function handleImageSelect(imageIndex) {
    if (onImageIndexChange) {
      onImageIndexChange(imageIndex);
    }
  }
};

ImageCarousel.propTypes = {
  feedback: PropTypes.array.isRequired,
  images: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    url: PropTypes.string.isRequired,
    page: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    alt: PropTypes.string,
  })),
  onBackClick: PropTypes.func.isRequired,
  selectedImageIndex: PropTypes.number,
  onImageIndexChange: PropTypes.func,
  onPinClick: PropTypes.func,
  isLoading: PropTypes.bool,
};

ImageCarousel.defaultProps = {
  feedback: [],
  images: [],
  onBackClick: () => {},
};

export default ImageCarousel;
